import { format } from "date-fns";
import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { Store, AssistantBroadcastMessage, LiveCall } from "./store.types";

// BroadcastChannel for cross-tab synchronization of assistant dialog
const assistantChannel = typeof window !== "undefined" && typeof BroadcastChannel !== "undefined" 
    ? new BroadcastChannel("assistant-dialog-sync") 
    : null;

const useStoreBase = create<Store>()(
  persist(
    (set) => ({
  recordsFilter: null,
  setRecordsFilter: (filters) => set(() => ({ recordsFilter: filters })),
  modalFilter: null,
  setModalFilters: (filters) => set(() => ({ modalFilter: filters })),
  progressFilter: null,
  setProgressFilter: (filters) => set(() => ({ progressFilter: filters })),
  clearEntityFilters: (entity) =>
    set((state) => {
      delete state.recordsFilter![entity];
      return { recordsFilter: state.recordsFilter };
    }),
  recordsSort: null,
  setRecordsSort: (sort) => set(() => ({ recordsSort: sort })),
  calendar: {
    defaultDate: new Date().toISOString(),
    setDefaultDate: (date) =>
      set((state) => ({ calendar: { ...state.calendar, defaultDate: format(date, "yyyy-MM-dd HH:mm:ss") } })),
    calendarView: null,
    setCalendarView: (calendarView) =>
      set((state) => ({ calendar: { ...state.calendar, calendarView: calendarView } })),
    calendarFilters: null,
    setCalendarFilters: (filters) =>
      set((state) => ({ calendar: { ...state.calendar, calendarFilters: filters } })),
    scheduleOverlayEnabled: false,
    setScheduleOverlayEnabled: (enabled) =>
      set((state) => ({ calendar: { ...state.calendar, scheduleOverlayEnabled: enabled } })),
    scheduleOverlayShowTimeOff: false,
    setScheduleOverlayShowTimeOff: (enabled) =>
      set((state) => ({ calendar: { ...state.calendar, scheduleOverlayShowTimeOff: enabled } })),
    scheduleOverlayDiscountBreaks: true,
    setScheduleOverlayDiscountBreaks: (enabled) =>
      set((state) => ({ calendar: { ...state.calendar, scheduleOverlayDiscountBreaks: enabled } })),
    scheduleOverlayShowBackoffice: false,
    setScheduleOverlayShowBackoffice: (enabled) =>
      set((state) => ({ calendar: { ...state.calendar, scheduleOverlayShowBackoffice: enabled } })),
    scheduleOverlayGhostBookings: false,
    setScheduleOverlayGhostBookings: (enabled) =>
      set((state) => ({ calendar: { ...state.calendar, scheduleOverlayGhostBookings: enabled } })),
  },
  schedules: {
    defaultDate: new Date().toISOString(),
    setDefaultDate: (date) =>
      set((state) => ({
        schedules: { ...state.schedules, defaultDate: format(date, "yyyy-MM-dd HH:mm:ss") },
      })),
    calendarView: "week",
    setCalendarView: (calendarView) =>
      set((state) => ({ schedules: { ...state.schedules, calendarView: calendarView } })),
    calendarFilters: null,
    setCalendarFilters: (filters) =>
      set((state) => ({ schedules: { ...state.schedules, calendarFilters: filters } })),
    servicesSelected: [],
    setServicesSelected: (services) =>
      set((state) => ({ schedules: { ...state.schedules, servicesSelected: services } })),
    eventTypeFilter: "all",
    setEventTypeFilter: (eventType) =>
      set((state) => ({ schedules: { ...state.schedules, eventTypeFilter: eventType } })),
    userIds: [],
    setUserIds: (userIds) => set((state) => ({ schedules: { ...state.schedules, userIds } })),
    resourceIds: [],
    setResourceIds: (resourceIds) => set((state) => ({ schedules: { ...state.schedules, resourceIds } })),
    clinicIds: [],
    setClinicIds: (clinicIds) => set((state) => ({ schedules: { ...state.schedules, clinicIds } })),
  },
  alerts: {
    isOpen: false,
    setIsOpen: (isOpen) => set((state) => ({ alerts: { ...state.alerts, isOpen: isOpen } })),
    allowExecution: false,
    setAllowExecution: (allowExecution) =>
      set((state) => ({ alerts: { ...state.alerts, allowExecution: allowExecution } })),
    title: "Tem a certeza que quer apagar?",
    setTitle: (title) => set((state) => ({ alerts: { ...state.alerts, title: title } })),
    message: "Esta ação é irreversivel! irá apagar este registo permanentemente.",
    setMessage: (message) => set((state) => ({ alerts: { ...state.alerts, message: message } })),
  },
  notifications: {
    notification: {
      isOpen: false,
    },
    setNotification: (notification) =>
      set((state) => ({ notifications: { ...state.notifications, notification: notification } })),
    notificationsList: {
      isOpen: false,
    },
    setNotificationsList: (notificationsList) =>
      set((state) => ({ notifications: { ...state.notifications, notificationsList: notificationsList } })),
  },
  menu: {
    isOpen: false,
    setIsOpen: (isOpen) => set((state) => ({ menu: { ...state.menu, isOpen: isOpen } })),
  },
  gridSettings: {
    gridColumns: [],
    selectedSettings: undefined,
    activeOnGrid: undefined,
    entity: null,
    apiSettings: null,
    configColumnsModalIsOpen: false,
    configColumnsModalAnchorEl: null,
    scrollPosition: {
      top: 0,
      left: 0,
      entity: "",
    },
    gridIsLoading: false,
    paginationModel: {
      page: 0,
      pageSize: 100,
    },
    setGridSettings: (newSettings) =>
      set((state) => {
        return {
          gridSettings: { ...state.gridSettings, ...newSettings },
        };
      }),
  },
  treatments: {
    view: "list",
    setView: (view) => set((state) => ({ treatments: { ...state.treatments, view: view } })),
  },
  quickSearchToolBar: {
    inputValue: {
      entity: "",
      inputValue: "",
    },
    setInputValue: (inputValue: { entity: string; inputValue: string }) =>
      set((state) => ({
        ...state,
        quickSearchToolBar: { ...state.quickSearchToolBar, inputValue: inputValue },
      })),
  },
  phoneClient: {
    iframeRef: null,
    url: "",
    setUrl: (url: string) => set((state) => ({ ...state, phoneClient: { ...state.phoneClient, url: url } })),
    setPhoneNumberToCall: (
      iframeRef: React.RefObject<HTMLIFrameElement>,
      url: string,
      phoneNumberToCall: string
    ) => {
      if (iframeRef.current && url) {
        iframeRef.current.contentWindow?.postMessage(
          {
            number: phoneNumberToCall,
            autoCall: "false",
          },
          url
        );
      }
    },
  },
  sms: {
    isOpen: false,
    setIsOpen: (isOpen: boolean) => set((state) => ({ ...state, sms: { ...state.sms, isOpen: isOpen } })),
    context: null,
    setContext: (context: any) => set((state) => ({ ...state, sms: { ...state.sms, context: context } })),
  },
  payment: {
    isOpen: false,
    setIsOpen: (isOpen: boolean) =>
      set((state) => ({ ...state, payment: { ...state.payment, isOpen: isOpen } })),
    context: null,
    setContext: (context: any) =>
      set((state) => ({ ...state, payment: { ...state.payment, context: context } })),
  },
  treatmentAndPaymentInformation: {
    isOpen: false,
    setIsOpen: (isOpen: boolean) =>
      set((state) => ({
        ...state,
        treatmentAndPaymentInformation: { ...state.treatmentAndPaymentInformation, isOpen: isOpen },
      })),
    context: null,
    setContext: (context: any) =>
      set((state) => ({
        ...state,
        treatmentAndPaymentInformation: { ...state.treatmentAndPaymentInformation, context: context },
      })),
  },
  evaluation_report_dialog: {
    isOpen: false,
    setIsOpen: (isOpen: boolean) =>
      set((state) => ({
        ...state,
        evaluation_report_dialog: { ...state.evaluation_report_dialog, isOpen: isOpen },
      })),
    context: null,
    setContext: (context: any) =>
      set((state) => ({
        ...state,
        evaluation_report_dialog: { ...state.evaluation_report_dialog, context: context },
      })),
    isLegacy: false,
    setIsLegacy: (isLegacy: boolean) =>
      set((state) => ({
        ...state,
        evaluation_report_dialog: { ...state.evaluation_report_dialog, isLegacy: isLegacy },
      })),
    isInstrumentsVersion: false,
    setIsInstrumentsVersion: (isInstrumentsVersion: boolean) =>
      set((state) => ({
        ...state,
        evaluation_report_dialog: { ...state.evaluation_report_dialog, isInstrumentsVersion: isInstrumentsVersion },
      })),
  },
  refetchFiles: {
    timestamp: 0,
    setTimestamp: (timestamp: number) =>
      set((state) => ({ ...state, refetchFiles: { ...state.refetchFiles, timestamp: timestamp } })),
  },
  staffWorkLogs: {
    selectedYear: new Date().getFullYear(),
    setSelectedYear: (year: number) =>
      set((state) => ({ staffWorkLogs: { ...state.staffWorkLogs, selectedYear: year } })),
    selectedMonth: new Date().getMonth(),
    setSelectedMonth: (month: number) =>
      set((state) => ({ staffWorkLogs: { ...state.staffWorkLogs, selectedMonth: month } })),
  },
  processesSearch: {
    isOpen: false,
    setIsOpen: (isOpen: boolean) =>
      set((state) => ({ ...state, processesSearch: { ...state.processesSearch, isOpen: isOpen } })),
  },
  order: {
    isOpen: false,
    setIsOpen: (isOpen: boolean) => set((state) => ({ ...state, order: { ...state.order, isOpen: isOpen } })),
    context: null,
    setContext: (context: any) => set((state) => ({ ...state, order: { ...state.order, context: context } })),
  },
  hideUIFromClient: {
    isHiddenFromClient: false,
    toggleIsHiddenFromClient: () =>
      set((state) => ({
        ...state,
        hideUIFromClient: {
          ...state.hideUIFromClient,
          isHiddenFromClient: !state.hideUIFromClient.isHiddenFromClient,
        },
      })),
  },
  appDialog: {
    isOpen: false,
    allowSoftCancel: false,
    maxWidth: "sm",
    toggleIsOpen: () => set((state) => ({...state, appDialog:{...state.appDialog, isOpen: !state.appDialog.isOpen}})),
    createDialog: ({ title, contentAndActions, allowSoftCancel = false, maxWidth = "sm", onSubmit, onCancel }) =>
      set((state) => ({
        appDialog: {
          ...state.appDialog,
          isOpen: true,
          title,
          contentAndActions,
          allowSoftCancel,
          maxWidth,
          onSubmit,
          onCancel,
        },
      })),
  },
  reservedSlotWarning: {
    resolve: null,
    setResolve: (resolve) =>
      set((state) => ({ reservedSlotWarning: { ...state.reservedSlotWarning, resolve } })),
    handleConfirm: (reason: string) =>
      set((state) => {
        state.reservedSlotWarning.resolve?.({ confirmed: true, reason });
        return { reservedSlotWarning: { ...state.reservedSlotWarning, resolve: null } };
      }),
    handleCancel: () =>
      set((state) => {
        state.reservedSlotWarning.resolve?.({ confirmed: false, reason: "" });
        return { reservedSlotWarning: { ...state.reservedSlotWarning, resolve: null } };
      }),
  },
  invoiceSchedulesDialog: {
    shouldOpen: false,
    triggerOpen: () =>
      set((state) => ({ invoiceSchedulesDialog: { ...state.invoiceSchedulesDialog, shouldOpen: true } })),
    resetTrigger: () =>
      set((state) => ({ invoiceSchedulesDialog: { ...state.invoiceSchedulesDialog, shouldOpen: false } })),
  },
  assistantDialog: {
    items: [],
    dismissedItemIds: [],
    dismissedDate: null,
    isMinimized: false,
    addItem: (item, broadcast = true) =>
      set((state) => {
        const itemExists = state.assistantDialog.items.some((existingItem) => existingItem.id === item.id);
        const wasDismissed = state.assistantDialog.dismissedItemIds.includes(item.id);
        if (itemExists || wasDismissed) return state;
        if (broadcast && assistantChannel) {
          assistantChannel.postMessage({ type: "addItem", item } as AssistantBroadcastMessage);
        }
        return {
          assistantDialog: {
            ...state.assistantDialog,
            items: [...state.assistantDialog.items, item],
          },
        };
      }),
    removeItem: (itemId, broadcast = true) =>
      set((state) => {
        if (broadcast && assistantChannel) {
          assistantChannel.postMessage({ type: "removeItem", itemId } as AssistantBroadcastMessage);
        }
        const todayDate = format(new Date(), "yyyy-MM-dd");
        return {
          assistantDialog: {
            ...state.assistantDialog,
            items: state.assistantDialog.items.filter((item) => item.id !== itemId),
            dismissedItemIds: [...state.assistantDialog.dismissedItemIds, itemId],
            dismissedDate: todayDate,
          },
        };
      }),
    removeItemsBySessionId: (sessionId, broadcast = true) =>
      set((state) => {
        if (broadcast && assistantChannel) {
          assistantChannel.postMessage({ type: "removeItemsBySessionId", sessionId } as AssistantBroadcastMessage);
        }
        return {
          assistantDialog: {
            ...state.assistantDialog,
            items: state.assistantDialog.items.filter((item) => item.sessionId !== sessionId),
          },
        };
      }),
    toggleMinimized: () =>
      set((state) => ({
        assistantDialog: {
          ...state.assistantDialog,
          isMinimized: !state.assistantDialog.isMinimized,
        },
      })),
  },
  liveCalls: {
    bySessionId: {},
    isMinimized: false,
    upsert: (call: LiveCall) =>
      set((state) => ({
        liveCalls: {
          ...state.liveCalls,
          bySessionId: { ...state.liveCalls.bySessionId, [call.sessionId]: call },
        },
      })),
    removeBySessionId: (sessionId: string) =>
      set((state) => {
        const nextBySessionId = { ...state.liveCalls.bySessionId };
        delete nextBySessionId[sessionId];
        return { liveCalls: { ...state.liveCalls, bySessionId: nextBySessionId } };
      }),
    removeById: (id: string) =>
      set((state) => {
        const matchedCall = Object.values(state.liveCalls.bySessionId).find((call) => call.id === id);
        if (!matchedCall) {
          return state;
        }
        const nextBySessionId = { ...state.liveCalls.bySessionId };
        delete nextBySessionId[matchedCall.sessionId];
        return { liveCalls: { ...state.liveCalls, bySessionId: nextBySessionId } };
      }),
    reset: (calls: Array<LiveCall>) =>
      set((state) => {
        const nextBySessionId: Record<string, LiveCall> = {};
        for (const call of calls) {
          nextBySessionId[call.sessionId] = call;
        }
        return { liveCalls: { ...state.liveCalls, bySessionId: nextBySessionId } };
      }),
    toggleMinimized: () =>
      set((state) => ({
        liveCalls: {
          ...state.liveCalls,
          isMinimized: !state.liveCalls.isMinimized,
        },
      })),
  },
}),
    {
      name: "neuroimprove-assistant",
      partialize: (state) => ({
        assistantDialog: {
          dismissedItemIds: state.assistantDialog.dismissedItemIds,
          dismissedDate: state.assistantDialog.dismissedDate,
          isMinimized: state.assistantDialog.isMinimized,
        },
        liveCalls: {
          isMinimized: state.liveCalls.isMinimized,
        },
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState as {
          assistantDialog?: { dismissedItemIds?: Array<string>; dismissedDate?: string | null; isMinimized?: boolean };
          liveCalls?: { isMinimized?: boolean };
        };
        const todayDate = format(new Date(), "yyyy-MM-dd");
        const isFromToday = persisted?.assistantDialog?.dismissedDate === todayDate;
        return {
          ...currentState,
          assistantDialog: {
            ...currentState.assistantDialog,
            dismissedItemIds: isFromToday ? (persisted?.assistantDialog?.dismissedItemIds ?? []) : [],
            dismissedDate: isFromToday ? todayDate : null,
            isMinimized: persisted?.assistantDialog?.isMinimized ?? false,
          },
          liveCalls: {
            ...currentState.liveCalls,
            isMinimized: persisted?.liveCalls?.isMinimized ?? false,
          },
        };
      },
    }
  )
);

// Export the hook for use in React components
export const useStore = useStoreBase;

// Export a function to access the store outside of React components
export const getStore = useStoreBase.getState;

// Hook to check if persist hydration is complete
export const useHasHydrated = () => {
    const [hasHydrated, setHasHydrated] = useState(useStoreBase.persist.hasHydrated());

    useEffect(() => {
        const unsubscribe = useStoreBase.persist.onFinishHydration(() => {
            setHasHydrated(true);
        });
        return () => unsubscribe();
    }, []);

    return hasHydrated;
};

// Listen for cross-tab broadcasts and apply changes
if (assistantChannel) {
  assistantChannel.onmessage = (event: MessageEvent<AssistantBroadcastMessage>) => {
    const message = event.data;
    const { assistantDialog } = getStore();

    if (message.type === "addItem") {
      assistantDialog.addItem(message.item, false);
    } else if (message.type === "removeItem") {
      assistantDialog.removeItem(message.itemId, false);
    } else if (message.type === "removeItemsBySessionId") {
      assistantDialog.removeItemsBySessionId(message.sessionId, false);
    }
  };
}
